import React, { useEffect, useRef, useState } from 'react';
import DebounceAsyncSelect from './DebounceSelect';
import './index.scss';
import { buildURL } from '@/fetch/core/util';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import StandardTileMap from '@/components/StandardTileMap';
import MapOperations from '@/utils/MapOperations';

const Navigation = (props: {
  vehicleName: string;
  handleNavigate: AnyFunc;
}) => {
  const { vehicleName } = props;
  const mapRef = useRef<any>(null); // 地图实例
  const mapOperateRef = useRef<MapOperations | null>(null); // 地图操作实例
  const timer = useRef<any>(null);
  const [startPoi, setStartPoi] = useState<number[]>([]);
  const [endPoi, setEndPoi] = useState<number[]>([]);
  const [mapReady, setMapReady] = useState<boolean>(false);

  // 获取车辆实时位置
  const getVehicleRealtimePoi = (cb?: AnyFunc) => {
    request({
      path: '/monitor/web/map/position',
      method: 'POST',
      body: {
        positionType: 'GCJ02',
        vehicleName,
        metaType: 'vehicle',
      },
    })
      .then((res: any) => {
        if (res?.code === HttpStatusCode.Success) {
          const { latitude, longitude, heading } = res?.data || {};

          if (mapOperateRef.current && latitude && longitude) {
            // OpenLayers使用[longitude, latitude]的顺序
            const coordinates = [longitude, latitude];
            mapOperateRef.current.drawVehiclePosition(coordinates, heading);

            // 如果有回调函数，执行回调
            cb && cb(coordinates);
          }

          // 定时获取车辆位置
          timer.current = setTimeout(() => {
            clearTimeout(timer.current);
            getVehicleRealtimePoi();
          }, 1000);
        }
      })
      .catch((e) => {
        console.error('Error fetching vehicle position:', e);
      });
  };

  // 获取路线规划
  const getPlanningRouting = (): Promise<any> => {
    return new Promise((resolve) => {
      if (
        !startPoi ||
        !endPoi ||
        startPoi.length !== 2 ||
        endPoi.length !== 2
      ) {
        resolve(null);
        return;
      }

      // 清除之前的标记和路线
      mapOperateRef.current?.clearAll();

      // 调用腾讯地图API获取路线规划
      (window as any).$.ajax({
        type: 'get',
        url: buildURL({
          absoluteURL: 'https://apis.map.qq.com/ws/direction/v1/driving/',
          urlParams: {
            key: '67ABZ-HLPRQ-XZM53-GNCSR-GNSXF-DSBDK',
            from: startPoi.toString(),
            to: endPoi.toString(),
            output: 'jsonp',
          },
        }),
        dataType: 'jsonp',
        success: (res: any) => {
          if (res.status === 0) {
            const pathArr: any[] = [];
            const polyline = res?.result?.routes[0]?.polyline || [];

            // 解码腾讯地图返回的压缩路径坐标
            for (let i = 2; i < polyline.length; i++) {
              polyline[i] = polyline[i - 2] + polyline[i] / 1000000;
            }

            // 构建路径点数组
            for (let i = 0; i < polyline.length; i += 2) {
              pathArr.push({
                lat: polyline[i],
                lng: polyline[i + 1],
              });
            }

            // 使用OpenLayers绘制路径
            if (mapOperateRef.current) {
              const olCoordinates =
                mapOperateRef.current.drawRoutingPath(pathArr);

              // 返回OpenLayers格式的坐标数组，用于在RealtimeMap上显示
              resolve(olCoordinates);
            } else {
              resolve(null);
            }
          } else {
            console.error('Error getting routing:', res);
            resolve(null);
          }
        },
        fail: (error: any) => {
          console.error('Error in routing API call:', error);
          resolve(null);
        },
      });
    });
  };

  // 初始化地图操作实例
  const initMapOperate = (map: any) => {
    mapRef.current = map;

    if (mapRef.current) {
      mapOperateRef.current = new MapOperations(mapRef.current);
      setMapReady(true);

      // 获取车辆实时位置
      getVehicleRealtimePoi();
    }
  };

  // 当起点和终点都设置后，获取路线规划
  useEffect(() => {
    if (mapReady && startPoi?.length === 2 && endPoi?.length === 2) {
      getPlanningRouting().then((res) => {
        if (res) {
          // 将路线规划结果传递给父组件
          props.handleNavigate(res);
        }
      });
    }
  }, [startPoi, endPoi, mapReady]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      clearTimeout(timer.current);
      timer.current = null;

      if (mapOperateRef.current) {
        mapOperateRef.current.clearAll();
      }
    };
  }, []);

  return (
    <div className="planning-container">
      <div className="poi-search">
        <div className="start-end-inputs">
          <div className="start-input-wrapper">
            <i className="start-icon"></i>
            <i className="label">起点</i>
            <DebounceAsyncSelect
              placeholder="请输入起点"
              onChange={(data: any) => {
                if (!data || !data.value) return;

                // 解析坐标，注意腾讯地图API返回的是"经度-纬度"格式，需要反转为[纬度,经度]
                const pos =
                  data.value
                    ?.split('-')
                    ?.reverse()
                    ?.map((i: string) => parseFloat(i)) || [];

                setStartPoi(pos);

                // 将地图中心设置为选择的起点
                if (mapOperateRef.current && pos.length === 2) {
                  // OpenLayers使用[longitude, latitude]的顺序，所以需要再次反转
                  mapOperateRef.current.changeCenter([pos[1], pos[0]]);
                }
              }}
            />
          </div>
          <div className="end-input-wrapper">
            <i className="end-icon"></i>
            <i className="label">终点</i>
            <DebounceAsyncSelect
              placeholder="输入终点"
              onChange={(data: any) => {
                if (!data || !data.value) return;

                // 解析坐标
                const pos = data.value
                  ?.split('-')
                  ?.reverse()
                  ?.map((i: string) => parseFloat(i));

                setEndPoi(pos);
              }}
            />
          </div>
        </div>
      </div>
      <div className="map-container">
        <StandardTileMap
          latitude={34.53506}
          longitude={108.93885}
          zoom={15}
          getMapRef={initMapOperate}
        />
      </div>
    </div>
  );
};

export default React.memo(Navigation);
